import * as THREE from "three";
import type { MaterialDef } from "../physics/materials";

/** A material's physical values plus its texture and its solid and preview looks. */
export interface Material extends MaterialDef {
  tex: THREE.CanvasTexture;
  visual: THREE.MeshStandardMaterial;
  ghost: THREE.MeshStandardMaterial;
}

/* ---------- Procedural textures drawn on a canvas ---------- */
function rand(a: number, b: number) {
  return a + Math.random() * (b - a);
}
function makeTexture(m: MaterialDef) {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const g = c.getContext("2d")!;
  g.fillStyle = m.color;
  g.fillRect(0, 0, 256, 256);
  if (m.id === "wood") {
    for (let i = 0; i < 26; i++) {
      g.strokeStyle = `rgba(90,50,20,${rand(0.15, 0.4)})`;
      g.lineWidth = rand(1, 3);
      g.beginPath();
      const y0 = rand(0, 256),
        amp = rand(2, 6),
        ph = rand(0, 6);
      for (let x = 0; x <= 256; x += 8) g.lineTo(x, y0 + Math.sin(x / 30 + ph) * amp);
      g.stroke();
    }
  } else if (m.id === "stone") {
    for (let i = 0; i < 2200; i++) {
      g.fillStyle =
        Math.random() < 0.5
          ? `rgba(40,40,45,${rand(0.1, 0.35)})`
          : `rgba(255,255,255,${rand(0.1, 0.3)})`;
      g.fillRect(rand(0, 256), rand(0, 256), rand(1, 3), rand(1, 3));
    }
  } else if (m.id === "ice") {
    g.strokeStyle = "rgba(255,255,255,0.6)";
    for (let i = 0; i < 14; i++) {
      g.lineWidth = rand(0.5, 2);
      g.beginPath();
      let x = rand(0, 256),
        y = rand(0, 256);
      g.moveTo(x, y);
      for (let k = 0; k < 4; k++) {
        x += rand(-40, 40);
        y += rand(-40, 40);
        g.lineTo(x, y);
      }
      g.stroke();
    }
  } else if (m.id === "rubber") {
    for (let i = 0; i < 1500; i++) {
      g.fillStyle = `rgba(0,0,0,${rand(0.03, 0.12)})`;
      g.fillRect(rand(0, 256), rand(0, 256), 2, 2);
    }
  } else if (m.id === "metal") {
    for (let y = 0; y < 256; y++) {
      g.fillStyle = `rgba(${Math.random() < 0.5 ? "255,255,255" : "60,65,75"},${rand(0.02, 0.12)})`;
      g.fillRect(0, y, 256, 1);
    }
    g.strokeStyle = "rgba(60,65,75,0.35)";
    g.lineWidth = 4;
    g.strokeRect(6, 6, 244, 244);
  } else if (m.id === "foam") {
    for (let i = 0; i < 220; i++) {
      g.fillStyle = `rgba(150,110,20,${rand(0.15, 0.35)})`;
      g.beginPath();
      g.arc(rand(0, 256), rand(0, 256), rand(1.5, 5), 0, Math.PI * 2);
      g.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export function createMaterials(defs: readonly MaterialDef[]): Material[] {
  return defs.map((m) => {
    const tex = makeTexture(m);
    return {
      ...m,
      tex,
      visual: new THREE.MeshStandardMaterial({
        map: tex,
        roughness: m.rough,
        metalness: m.metal,
        transparent: !!m.opacity,
        opacity: m.opacity || 1,
      }),
      ghost: new THREE.MeshStandardMaterial({
        map: tex,
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
      }),
    };
  });
}
